import { useState, useMemo, useCallback, useEffect } from 'react';
import { Store } from '../../../types';
import { useSharedStores } from '../hooks';

export function useStoreSelector(
  multiple: boolean = false,
  value: Store | Store[] | null = multiple ? [] : null,
  onChange: (value: Store | Store[] | null) => void
) {
  const { allStores, isLoading, error: apiError } = useSharedStores();
  const [inputValue, setInputValue] = useState('');

  // initialize multiple value to empty array
  useEffect(() => {
    if (multiple && !value) onChange([]);
  }, [multiple, value, onChange]);

  // parse raw text into individual storeNumbers
  const parseInputs = useCallback((text: string): string[] => {
    return text
      .trim()
      .split(/[\s,]+/)     // split on comma, space, newline
      .map(s => s.trim())
      .filter(Boolean);
  }, []);

  // given an array of storeNumbers, find matching Store objects
  const lookupStores = useCallback((codes: string[]): Store[] => {
    if (!allStores) return [];
    return codes
      .map(code => allStores.find(s => s.storeNumber === code))
      .filter((s): s is Store => !!s);
  }, [allStores]);

  // handle splitting current inputValue into chips
  const commitChips = useCallback(() => {
    const codes = parseInputs(inputValue);
    if (codes.length === 0) return;
    const found = lookupStores(codes);
    if (multiple) {
      // append to existing
      const existing = Array.isArray(value) ? value : [];
      const merged = [...existing];
      found.forEach(s => {
        if (!merged.some(e => e.id === s.id)) merged.push(s);
      });
      onChange(merged);
    } else {
      // single-select: pick first
      onChange(found[0] || null);
    }
    setInputValue('');
  }, [inputValue, parseInputs, lookupStores, multiple, onChange, value]);

  // handle typing Enter, comma or space
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitChips();
    }
  };

  // handle pasting a block of codes
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const codes = parseInputs(text);
    if (codes.length === 0) return;
    
    const found = lookupStores(codes);
    if (multiple) {
      // append to existing
      const existing = Array.isArray(value) ? value : [];
      const merged = [...existing];
      found.forEach(s => {
        if (!merged.some(e => e.id === s.id)) merged.push(s);
      });
      onChange(merged);
    } else {
      // single-select: pick first
      onChange(found[0] || null);
    }
    setInputValue('');
  };

  // filter out already‐selected stores
  const filteredOptions = useMemo<Store[]>(() => {
    if (!allStores) return [];
    const lower = inputValue.toLowerCase();
    let list = allStores;
    if (lower) {
      list = list.filter(s =>
        s.storeNumber.toLowerCase().includes(lower) ||
        (s.name && s.name.toLowerCase().includes(lower))
      );
    }
    if (multiple && Array.isArray(value)) {
      return list.filter(
        s => !value.some(sel => sel.id === s.id)
      );
    }
    return list;
  }, [allStores, inputValue, multiple, value]);

  const handleChange = useCallback((newVal: (Store | string)[]) => {
    if (multiple) {
      // normalize array of mixed Store|string
      const stores: Store[] = [];
      newVal.forEach(v => {
        if (typeof v === 'string') {
          lookupStores([v]).forEach(s => stores.push(s));
        } else {
          stores.push(v);
        }
      });
      onChange(stores);
    } else {
      const v = (newVal as Store[])[0];
      onChange(v || null);
    }
  }, [multiple, onChange, lookupStores]);

  const error = apiError ? 'Failed to load stores' : undefined;

  return {
    inputValue,
    setInputValue,
    filteredOptions,
    handleKeyDown,
    handlePaste,
    handleChange,
    isLoading,
    error,
  };
}